import React from "react";
import { useParams, Link } from "react-router-dom";
import { useApolloClient, useMutation, useQuery, gql } from "@apollo/client";
import { FileText, SortDesc, Filter, Printer, Pencil, Save, Eye, Trash2, Plus } from "lucide-react";
import { TabHeader } from "../shared/components/TabHeader";
import { TabToolbar } from "../shared/components/TabToolbar";
import { TabTable } from "../shared/components/TabTable";
import { TabSpinner } from "../shared/components/TabSpinner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/toast";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { FormInput } from "../../participantDocuments/lib/formRender";
import { FormView } from "../../participantDocuments/lib/formView";
import { FormCarePlan } from "../../participantDocuments/legacy/careplan";
import { FormInitialAssessment } from "../../participantDocuments/legacy/initialassessment";
import { getFormBehavior } from "../../participantDocuments/behaviors/registry";
import { SearchParticipants } from "../../participantDocuments/search";
import { useUser } from "@/store/useUser";
import { useUserPermissions } from "@/store/useUserPermissions";
import { useSetHelpTopic } from "@/store/useHelpTopics";
import { hasAnyPolicy, policyMatches } from "@/utils/policies";

const DELETE_PARTICIPANT_DOCUMENT = gql`
  mutation DeleteParticipantDocument($id: Int!, $documentType: String) {
    deleteParticipantDocument(id: $id, documentType: $documentType) {
      id
    }
  }
`;

const GET_PARTICIPANT_DOCUMENT = gql`
  query GetParticipantDocument($documentID: Int!) {
    getParticipantDocument(documentID: $documentID) {
      id
      odrparticipant
      contents
      template
      tags
      type
      metadata
    }
  }
`;

const UPDATE_PARTICIPANT_DOCUMENT = gql`
  mutation UpdateParticipantDocument($id: Int!, $updates: GenericJSONDocumentInput!) {
    updateParticipantDocument(id: $id, updates: $updates) {
      id
      contents
      published
      tags
      metadata
      type
      date
      odrparticipant
      template
    }
  }
`;

const CREATE_PARTICIPANT_DOCUMENT = gql`
  mutation CreateParticipantDocument($updates: GenericJSONDocumentInput!) {
    createParticipantDocument(updates: $updates) {
      id
      contents
      published
      tags
      metadata
      type
      date
      odrparticipant
      template
    }
  }
`;

const LIST_FORM_TEMPLATES = gql`
  query listFormTemplates {
    listFormTemplates {
      id
      name
      body
    }
  }
`;

const TRANSFER_PARTICIPANT_DOCUMENTS = gql`
  mutation TransferParticipantDocuments($odrnumber: Int!, $documentID: [String]) {
    transferParticipantDocuments(odrnumber: $odrnumber, documentID: $documentID)
  }
`;

const GET_PARTICIPANT_BY_ODRNUMBER = gql`
  query GetParticipantByODRNumber($odrnumber: Int!) {
    getParticipantByODRNumber(odrnumber: $odrnumber) {
      id
      name {
        firstName
        lastName
      }
      cin
      documents {
        id
        published
        tags
        type
        metadata
        date
        contents
        template
      }
    }
  }
`;

const GET_PARTICIPANT_CAREPLAN_GOALS = gql`
  query GetParticipantCarePlanGoals($participantID: String!) {
    getParticipantCarePlanGoals(participantID: $participantID) {
      start_date
      due_date
      problem
      priority
      identified_strengths
      identified_barriers
      smart_goal
      intervention_steps
      goal_status
      outcome_narrative
    }
  }
`;

type ParticipantDocument = {
  id: number | string;
  type: string;
  date?: string | null;
  tags?: string[];
  contents?: string | null;
  template?: string | null;
  metadata?: Record<string, unknown>;
  published?: number;
};

const alias: Record<string, string> = {
  LACAREREF: "LACare Referral",
  LACARE_Initial_Assessment: "Imported Initial Assessment",
  LACARE_Care_Plan: "Imported Care Plan",
  LACAREProgram_Completion: "Imported Program Completion",
};

const formatDate = (date?: string | null) => {
  if (!date) return "";
  const [year, month, day] = date.substring(0, 10).split("-");
  return `${month}-${day}-${year}`;
};

const parseJsonSafe = (value?: string | null, fallback: any = {}) => {
  if (!value) return fallback;
  try {
    return JSON.parse(value);
  } catch (_error) {
    return fallback;
  }
};

type SortMode = "date_desc" | "type" | "type_date";

const DOCUMENTS_ADMIN_POLICIES = [
  "admin:access~full",
  "tools:billing:access~full",
  "admin:access~full",
];
const DOCUMENT_EDIT_POLICIES = [
  "program:participant:documentscans~full",
];
const DOCUMENT_DELETE_POLICIES = [
  "program:participant:documentscans~full",
  "admin:access~full",
  "tools:billing:access~full",
];

export const DocumentsTab: React.FC<{ odrnumber?: number | string }> = ({ odrnumber }) => {
  const { odrparticipant: routeParticipant } = useParams();
  const participantId = odrnumber ?? routeParticipant;
  const participantIdString = participantId?.toString() || "0";
  const participantIdNumber = parseInt(participantIdString, 10);
  useSetHelpTopic("Participant Documents");

  const [createDocument] = useMutation(CREATE_PARTICIPANT_DOCUMENT);
  const [deleteDocument] = useMutation(DELETE_PARTICIPANT_DOCUMENT);
  const [updateDocument] = useMutation(UPDATE_PARTICIPANT_DOCUMENT);
  const [transferDocuments] = useMutation(TRANSFER_PARTICIPANT_DOCUMENTS);

  const client = useApolloClient();
  const { user, isUserLoading } = useUser();
  const { policies } = useUserPermissions();

  const [participant, setParticipant] = React.useState({
    name: { firstName: "", lastName: "" },
    documents: [] as ParticipantDocument[],
    cin: "",
  });

  const [isAltView, setIsAltView] = React.useState(false);
  const [currentSelectedType, setCurrentSelectedType] = React.useState("");
  const [newDocumentState, setNewDocumentState] = React.useState<any>();
  const [newDocumentModal, setNewDocumentModal] = React.useState(false);
  const [transferDocumentModal, setTransferDocumentModal] = React.useState(false);
  const [currentDocumentID, setCurrentDocumentID] = React.useState<number | null>(null);
  const [currentDocument, setCurrentDocument] = React.useState<{ id: number | null; template: any }>({
    id: null,
    template: [],
  });
  const [currentDocumentReadOnly, setCurrentDocumentReadOnly] = React.useState(false);
  const [printView, setPrintView] = React.useState(false);
  const [editMode, setEditMode] = React.useState(false);
  const [adminView, setAdminView] = React.useState(false);
  const [selectDocuments, setSelectDocuments] = React.useState<string[]>([]);
  const [legacyDocument, setLegacyDocument] = React.useState({ id: null as number | null, type: "" });
  const [typeFilter, setTypeFilter] = React.useState("all");
  const [sortMode, setSortMode] = React.useState<SortMode>("date_desc");
  const canUseDocumentsAdmin = policies.some((policy: string) => {
    if (policyMatches(policy, "*:*~*")) return true;
    return DOCUMENTS_ADMIN_POLICIES.some((requiredPolicy) => policyMatches(policy, requiredPolicy));
  });
  const canEditDocument = hasAnyPolicy(policies, DOCUMENT_EDIT_POLICIES);
  const canDeleteDocument = hasAnyPolicy(policies, DOCUMENT_DELETE_POLICIES);

  const buildTransferDocumentID = (document: ParticipantDocument) => {
    if (document.type === "LACARE_Initial_Assessment") return `i${document.id}`;
    if (document.type === "LACARE_Care_Plan") return `c${document.id}`;
    if (document.type === "LACAREProgram_Completion") return `p${document.id}`;
    return `${document.id}`;
  };
  const getSelectionKey = (document: ParticipantDocument) => `${document.type}:${document.id}`;

  const { loading: templateLoading, error: templateError, data: docList } = useQuery(LIST_FORM_TEMPLATES);

  const { loading, error, refetch } = useQuery(GET_PARTICIPANT_BY_ODRNUMBER, {
    variables: { odrnumber: participantIdNumber },
    skip: !participantId,
    onCompleted: (data) => {
      const participantData = data.getParticipantByODRNumber || {
        name: { firstName: "", lastName: "" },
        documents: [],
        cin: "",
      };
      if (!participantData.name) participantData.name = { firstName: "", lastName: "" };
      if (!participantData.documents) participantData.documents = [];
      setParticipant(participantData);
    },
  });

  useQuery(GET_PARTICIPANT_DOCUMENT, {
    variables: { documentID: currentDocumentID || null },
    skip: currentDocumentID == null || currentDocumentReadOnly,
    onCompleted: (data) => {
      setCurrentDocument({
        id: data.getParticipantDocument.id,
        template: JSON.parse(data.getParticipantDocument.template),
      });
      setNewDocumentState(JSON.parse(data.getParticipantDocument.contents));
      setCurrentDocumentReadOnly(false);
    },
  });

  React.useEffect(() => {
    if (!printView) return;
    window.print();
    setPrintView(false);
  }, [printView]);

  React.useEffect(() => {
    if (canUseDocumentsAdmin) return;
    setAdminView(false);
  }, [canUseDocumentsAdmin]);

  if (loading || templateLoading || isUserLoading) return <TabSpinner label="Loading documents..." />;
  if (error || templateError) {
    return (
      <Card>
        <CardContent className="text-sm text-red-600">
          Error loading documents data.
        </CardContent>
      </Card>
    );
  }

  const templateTypesFull = (docList?.listFormTemplates || []).map((template: any) => ({ ...template }));
  templateTypesFull.forEach((template: any) => {
    if (template.name !== "Care Plan") return;
    const altTemplate = templateTypesFull.find((t: any) => t.name === "_Care Plan");
    if (!altTemplate) return;
    template.altTemplate = altTemplate.body;
  });
  const templateTypes = templateTypesFull.filter((template: any) => !template.name.startsWith("_"));

  const templatesByType = templateTypesFull.reduce((acc: Record<string, any>, template: any) => {
    acc[template.name] = template;
    return acc;
  }, {});

  const findMostRecentCarePlan = () => {
    const carePlans = participant.documents.filter((doc: any) => doc.type === "Care Plan");
    if (carePlans.length === 0) return null;
    return carePlans.reduce((mostRecent: any, current: any) => {
      if (new Date(current.date || "") > new Date(mostRecent.date || "")) return current;
      return mostRecent;
    });
  };

  const importPreviousCarePlan = async () => {
    const mostRecentCarePlan = findMostRecentCarePlan();
    if (!mostRecentCarePlan) {
      toast.error("No previous Care Plan found");
      return;
    }

    try {
      const { data } = await client.query({
        query: GET_PARTICIPANT_DOCUMENT,
        variables: { documentID: mostRecentCarePlan.id },
      });
      if (!data?.getParticipantDocument) return;
      setNewDocumentState(JSON.parse(data.getParticipantDocument.contents));
      toast.success("Previous Care Plan imported");
    } catch (err) {
      toast.error("Error importing previous Care Plan");
    }
  };

  const getFormTemplate = (formType: string) => {
    const behavior = getFormBehavior(formType);
    const templateData = templatesByType[formType];
    if (behavior.getTemplate) return behavior.getTemplate(templateData, isAltView);
    return templateData?.body || "{}";
  };

  const renderFormHeader = (formType: string) => {
    const behavior = getFormBehavior(formType);
    if (!behavior.renderHeader) return null;
    return behavior.renderHeader({
      importPreviousCarePlan,
      isAltView,
      setIsAltView,
      currentSelectedType,
      templateData: templatesByType[formType],
    });
  };

  const startTransfer = async (participantToTransfer: any) => {
    const confirmed = window.confirm(
      `Are you sure you want to transfer these document to ODR: ${participantToTransfer.id} CID: ${participantToTransfer.cid} ?`
    );
    if (!confirmed) return;
    const selectedDocuments = (participant.documents || []).filter((doc: ParticipantDocument) =>
      selectDocuments.includes(getSelectionKey(doc))
    );
    const documentID = selectedDocuments.map((doc) => buildTransferDocumentID(doc));
    await transferDocuments({
      variables: {
        odrnumber: participantToTransfer.id,
        documentID,
      },
    });
    setTransferDocumentModal(false);
    refetch();
  };

  const createNewDocument = async () => {
    if (!currentSelectedType) {
      toast.error("Please select a document type");
      return;
    }
    const behavior = getFormBehavior(currentSelectedType);
    let initialFormState: any = {};
    if (behavior.getInitialData) initialFormState = behavior.getInitialData({ participant, user });
    if (currentSelectedType === "Care Plan") {
      try {
        const { data: goalsData } = await client.query({
          query: GET_PARTICIPANT_CAREPLAN_GOALS,
          variables: { participantID: participantId?.toString() || "" },
        });
        const goals = goalsData?.getParticipantCarePlanGoals || [];
        if (goals.length > 0) {
          initialFormState.goals = goals.map((goal: any) => ({
            start_date: goal.start_date || "",
            due_date: goal.due_date || "",
            problem: goal.problem || "",
            priority: goal.priority || "",
            identified_strengths: goal.identified_strengths || "",
            identified_barriers: goal.identified_barriers || "",
            smart_goal: goal.smart_goal || "",
            intervention_steps: goal.intervention_steps || "",
            goal_status: goal.goal_status || "in_progress",
            outcome_narrative: goal.outcome_narrative || "",
            import_to_next: false,
          }));
          toast.success(`Imported ${goals.length} incomplete goals from previous care plans`);
        }
      } catch (err) {
        // proceed without imported goals
      }
    }
    setNewDocumentState(initialFormState);
    setNewDocumentModal(true);
  };

  const handleDeleteDocument = async () => {
    if (!currentDocument?.id) return;
    const confirmed = window.confirm("Are you sure you want to delete this document?");
    if (!confirmed) return;
    try {
      await deleteDocument({
        variables: {
          id: currentDocument.id,
          documentType: currentSelectedType || null,
        },
      });
      toast.success("Document Updated");
      setCurrentDocumentID(null);
      setCurrentDocumentReadOnly(false);
      setEditMode(false);
      refetch();
    } catch (error: any) {
      toast.error(error?.message || "Unable to delete document");
    }
  };

  const handleEditDocument = async () => {
    if (!currentDocument?.id) return;
    try {
      await updateDocument({
        variables: {
          id: currentDocument.id,
          updates: {
            contents: JSON.stringify(newDocumentState),
            published: 0,
            metadata: "{}",
            type: currentSelectedType,
            odrparticipant: participantIdNumber,
            template: JSON.stringify(currentDocument.template),
          },
        },
      });
      toast.success("Document Updated");
      setCurrentDocumentID(null);
      setCurrentDocumentReadOnly(false);
      setEditMode(false);
      refetch();
    } catch (error: any) {
      toast.error(error?.message || "Unable to save document");
    }
  };

  const handleCreateDocument = async () => {
    try {
      await createDocument({
        variables: {
          updates: {
            contents: JSON.stringify(newDocumentState),
            published: 0,
            metadata: "{}",
            type: currentSelectedType,
            odrparticipant: participantIdNumber,
            template: getFormTemplate(currentSelectedType),
          },
        },
      });
      toast.success("Document Created");
      refetch();
    } catch (error: any) {
      toast.error(error?.message || "Unable to create document");
    }
  };

  const viewDocument = (document: ParticipantDocument) => {
    if (document.type === "LACARE_Initial_Assessment") {
      const numericDocumentID = Number.parseInt(`${document.id}`, 10);
      if (Number.isNaN(numericDocumentID)) {
        toast.error("Unable to view this assessment");
        return;
      }
      setLegacyDocument({ type: "inital", id: numericDocumentID });
      return;
    }
    if (document.type === "LACARE_Care_Plan") {
      const numericDocumentID = Number.parseInt(`${document.id}`, 10);
      if (Number.isNaN(numericDocumentID)) {
        toast.error("Unable to view this care plan");
        return;
      }
      setCurrentSelectedType("Care Plan");
      setCurrentDocument({
        id: numericDocumentID,
        template: parseJsonSafe(getFormTemplate("Care Plan"), []),
      });
      setNewDocumentState(parseJsonSafe(document.contents, {}));
      setCurrentDocumentReadOnly(true);
      setCurrentDocumentID(numericDocumentID);
      return;
    }

    setCurrentSelectedType(document.type);
    setCurrentDocumentReadOnly(false);
    const numericDocumentID = Number.parseInt(`${document.id}`, 10);
    if (Number.isNaN(numericDocumentID)) {
      toast.error("Unable to view this document in the current viewer");
      return;
    }
    setCurrentDocumentID(numericDocumentID);
  };

  const documentsWithDisplayType = (participant.documents || []).map((doc: any) => ({
    ...doc,
    displayType: alias[doc.type] || doc.type,
  }));

  const availableTypes = Array.from(
    new Set<string>([
      ...documentsWithDisplayType.map((doc) => doc.displayType),
      ...templateTypes.map((t: any) => t.name),
    ])
  ).filter(Boolean);

  const filteredDocuments = documentsWithDisplayType.filter((doc) => {
    if (typeFilter === "all") return true;
    return doc.displayType === typeFilter;
  });

  const sortedDocuments = [...filteredDocuments].sort((a, b) => {
    if (sortMode === "date_desc") {
      return new Date(b.date || "").getTime() - new Date(a.date || "").getTime();
    }
    if (sortMode === "type") {
      return (a.displayType || "").localeCompare(b.displayType || "");
    }
    return (a.displayType || "").localeCompare(b.displayType || "") || new Date(b.date || "").getTime() - new Date(a.date || "").getTime();
  });

  const toolbarFilters = (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-1">
        <Filter className="h-4 w-4 text-gray-500" />
        <label htmlFor="participant-documents-filter" className="text-sm text-gray-700">
          Type
        </label>
        <select
          id="participant-documents-filter"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="h-9 rounded-md border border-gray-300 bg-white px-2 text-sm"
        >
          <option value="all">All</option>
          {availableTypes.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>
      </div>
      <div className="flex items-center gap-1">
        <SortDesc className="h-4 w-4 text-gray-500" />
        <label htmlFor="participant-documents-sort" className="text-sm text-gray-700">
          Sort
        </label>
        <select
          id="participant-documents-sort"
          value={sortMode}
          onChange={(e) => setSortMode(e.target.value as SortMode)}
          className="h-9 rounded-md border border-gray-300 bg-white px-2 text-sm"
        >
          <option value="date_desc">Date (Newest)</option>
          <option value="type">Type</option>
          <option value="type_date">Type + Date</option>
        </select>
      </div>
    </div>
  );

  const toolbarActions = (
    <div className="flex items-center gap-2">
      {canUseDocumentsAdmin && (
        <Button
          id="participant-documents-admin-toggle"
          variant="outline"
          size="sm"
          onClick={() => setAdminView(!adminView)}
        >
          {adminView ? "Exit Admin" : "Admin"}
        </Button>
      )}
      {canUseDocumentsAdmin && adminView && (
        <Button
          id="participant-documents-transfer"
          variant="secondary"
          size="sm"
          onClick={() => setTransferDocumentModal(true)}
        >
          Transfer
        </Button>
      )}
    </div>
  );

  const creationControls = (
    <div className="flex items-center gap-2">
      <select
        id="participant-documents-create-type"
        value={currentSelectedType}
        onChange={(e) => setCurrentSelectedType(e.target.value)}
        className="h-8 rounded-md border border-gray-300 bg-white px-2 text-xs outline-none focus:ring-1 focus:ring-blue-500"
      >
        <option value="">Select document type...</option>
        {templateTypes.map((template: any) => (
          <option key={template.id} value={template.name}>
            {template.name}
          </option>
        ))}
      </select>
      <Button 
        id="participant-documents-create" 
        size="sm" 
        onClick={createNewDocument}
        className="h-8 gap-1.5 shadow-sm"
      >
        <Plus className="h-3.5 w-3.5" />
        New Document
      </Button>
    </div>
  );

  const documentTable = (
    <TabTable<ParticipantDocument & { displayType: string }>
      data={sortedDocuments}
      emptyMessage="No documents found for this participant."
      columns={[
        {
          title: "Date",
          className: "w-[140px]",
          render: (doc) => formatDate(doc.date),
        },
        {
          title: "Type",
          className: "w-[220px]",
          render: (doc) => doc.displayType,
        },
        {
          title: "Tags",
          render: (doc) => (doc.tags && doc.tags.length > 0 ? doc.tags.join(", ") : "—"),
        },
        {
          title: "Actions",
          className: "w-[140px]",
          render: (doc) => (
            <div className="flex items-center gap-2">
              {adminView && (
                <input
                  id={`participant-documents-select-${doc.id}`}
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300"
                  checked={selectDocuments.includes(getSelectionKey(doc))}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectDocuments([...selectDocuments, getSelectionKey(doc)]);
                      return;
                    }
                    setSelectDocuments(selectDocuments.filter((selectionKey) => selectionKey !== getSelectionKey(doc)));
                  }}
                />
              )}
              <Button
                id={`participant-documents-view-${doc.id}`}
                size="sm"
                variant="secondary"
                onClick={() => viewDocument(doc)}
              >
                <Eye className="mr-2 h-4 w-4" />
                View
              </Button>
            </div>
          ),
        },
      ]}
    />
  );

  const participantDetailsCard = (
    <Card>
      <CardContent className="space-y-2 pt-4">
        <div className="text-sm font-medium text-gray-900">Participant Information</div>
        <div className="text-sm text-gray-700">ODR Number: {participantId}</div>
        <div className="text-sm text-gray-700">
          Name: {participant.name.firstName} {participant.name.lastName}
        </div>
        <div className="text-sm text-gray-700">CIN: {participant.cin}</div>
        {canUseDocumentsAdmin && (
          <Link to="/admin/forms">
            <Button id="participant-documents-edit-templates" variant="outline" size="sm">
              Edit Form Templates
            </Button>
          </Link>
        )}
      </CardContent>
    </Card>
  );

  if (printView) {
    const printStyle = {
      position: "fixed",
      top: "0",
      right: "0",
      bottom: "0",
      left: "0",
      backgroundColor: "white",
      zIndex: "9999",
    } as React.CSSProperties;
    if (legacyDocument.type === "inital") return <div style={printStyle}><FormInitialAssessment id={legacyDocument.id} /></div>;
    if (legacyDocument.type === "careplan") return <div style={printStyle}><FormCarePlan id={legacyDocument.id} /></div>;
    return <FormView print formTemplate={currentDocument.template} formState={newDocumentState} />;
  }

  return (
    <div className="p-6 h-full overflow-y-auto bg-white space-y-4">
      <TabHeader
        icon={FileText}
        title="Documents"
        subtitle="Participant documents, care plans, and assessments"
        action={creationControls}
      />

      <TabToolbar left={toolbarFilters} right={toolbarActions} />

      <Card className="rounded-none border-0 shadow-none">
        <CardContent className="p-0">
          <div className="border-t border-b border-gray-200">
            {documentTable}
          </div>
        </CardContent>
      </Card>

      {/* {participantDetailsCard} */}

      {newDocumentModal && (
        <Dialog open={newDocumentModal} onOpenChange={setNewDocumentModal}>
          <DialogContent 
            className="sm:max-w-[80%] w-[80%] h-[90%] flex flex-col p-0 gap-0"
            onPointerDownOutside={(e) => e.preventDefault()}
            onEscapeKeyDown={(e) => e.preventDefault()}
          >
            <div className="flex-none p-4 border-b flex items-center justify-between bg-white">
              <h2 className="text-lg font-semibold text-gray-800">New {currentSelectedType}</h2>
            </div>
            <div className="flex-1 overflow-y-auto p-6 bg-gray-50/30">
              {renderFormHeader(currentSelectedType)}
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
                <FormInput
                  formTemplate={JSON.parse(getFormTemplate(currentSelectedType))}
                  formState={newDocumentState}
                  setFormState={setNewDocumentState}
                  onSubmit={handleCreateDocument}
                  onClose={() => {}}
                />
              </div>
            </div>
            <div className="flex-none p-4 border-t flex items-center justify-between bg-gray-50 rounded-b-lg">
              <Button 
                id="participant-documents-save-new" 
                size="sm"
                className="gap-1.5 shadow-sm px-6"
                onClick={() => { setNewDocumentModal(false); handleCreateDocument(); }}
              >
                <Save className="h-4 w-4" />
                Save Document
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setNewDocumentModal(false)}
              >
                Cancel
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {currentDocumentID && (
        <Dialog 
          open={!!currentDocumentID}
          onOpenChange={(open: boolean) => {
            if (!open) {
              setCurrentDocumentID(null);
              setCurrentDocumentReadOnly(false);
              setEditMode(false);
            }
          }}
        >
          <DialogContent 
            className="sm:max-w-[80%] w-[80%] h-[90%] flex flex-col p-0 gap-0"
            onPointerDownOutside={(e) => e.preventDefault()}
            onEscapeKeyDown={(e) => e.preventDefault()}
          >
            <div className="flex-none p-4 border-b flex items-center justify-between bg-white">
              <h2 className="text-lg font-semibold text-gray-800">
                {editMode ? "Edit Document" : "View Document"}
              </h2>
            </div>
            <div className="flex-1 overflow-y-auto p-6 bg-gray-50/30">
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
                {editMode ? (
                  <FormInput
                    formTemplate={currentDocument.template}
                    formState={newDocumentState}
                    setFormState={setNewDocumentState}
                    onSubmit={handleEditDocument}
                    onClose={() => {}}
                  />
                ) : (
                  <FormView
                    formTemplate={currentDocument.template}
                    formState={newDocumentState}
                    setFormState={setNewDocumentState}
                    onSubmit={handleCreateDocument}
                    onClose={() => {}}
                  />
                )}
              </div>
            </div>
            <div className="flex-none p-4 border-t flex items-center justify-between bg-gray-50 rounded-b-lg">
              {editMode ? (
                <>
                  <div className="flex gap-3">
                    {canDeleteDocument && (
                      <Button
                        id="participant-documents-delete"
                        type="button"
                        variant="destructive"
                        size="sm"
                        className="gap-1.5"
                        onClick={handleDeleteDocument}
                      >
                        <Trash2 className="h-4 w-4" />
                        Delete
                      </Button>
                    )}
                    <Button
                      id="participant-documents-view-mode"
                      type="button"
                      variant="outline"
                      size="sm"
                      className="gap-1.5"
                      onClick={() => setEditMode(false)}
                    >
                      <Eye className="h-4 w-4" />
                      View
                    </Button>
                    <Button
                      id="participant-documents-save-edit"
                      type="button"
                      size="sm"
                      className="gap-1.5 shadow-sm px-6"
                      onClick={handleEditDocument}
                    >
                      <Save className="h-4 w-4" />
                      Save Changes
                    </Button>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setEditMode(false)}
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setCurrentDocumentID(null);
                      setCurrentDocumentReadOnly(false);
                    }}
                  >
                    Close
                  </Button>
                  <div className="flex gap-3">
                    <Button
                      id="participant-documents-print"
                      type="button"
                      variant="outline"
                      size="sm"
                      className="gap-1.5"
                      onClick={() => setPrintView(true)}
                    >
                      <Printer className="h-4 w-4" />
                      Print
                    </Button>
                    {canEditDocument && !currentDocumentReadOnly && (
                      <Button
                        id="participant-documents-edit"
                        type="button"
                        size="sm"
                        className="gap-1.5 shadow-sm"
                        onClick={() => setEditMode(true)}
                      >
                        <Pencil className="h-4 w-4" />
                        Edit Document
                      </Button>
                    )}
                  </div>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {legacyDocument.type !== "" && (
        <Dialog 
          open={legacyDocument.type !== ""} 
          onOpenChange={(open: boolean) => {
            if (!open) {
              setLegacyDocument({ type: "", id: null });
              setEditMode(false);
            }
          }}
        >
          <DialogContent 
            className="sm:max-w-[80%] w-[80%] h-[90%] flex flex-col p-0 gap-0"
            onPointerDownOutside={(e) => e.preventDefault()}
            onEscapeKeyDown={(e) => e.preventDefault()}
          >
            <div className="flex-none p-4 border-b flex items-center justify-between bg-white">
              <h2 className="text-lg font-semibold text-gray-800">Legacy Document</h2>
            </div>
            <div className="flex-1 overflow-y-auto p-6 bg-gray-50/30">
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
                {legacyDocument.type === "inital" && <FormInitialAssessment id={legacyDocument.id} />}
                {legacyDocument.type === "careplan" && <FormCarePlan id={legacyDocument.id} />}
              </div>
            </div>
            <div className="flex-none p-4 border-t flex items-center justify-between bg-gray-50 rounded-b-lg">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setLegacyDocument({ type: "", id: null })}
              >
                Close
              </Button>
              <Button
                id="participant-documents-legacy-print"
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => setPrintView(true)}
              >
                <Printer className="mr-2 h-4 w-4" />
                Print
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {transferDocumentModal && (
        <Dialog open={transferDocumentModal} onOpenChange={setTransferDocumentModal}>
          <DialogContent 
            className="sm:max-w-[80%] w-[80%] h-[90%] flex flex-col p-0 gap-0"
            onPointerDownOutside={(e) => e.preventDefault()}
            onEscapeKeyDown={(e) => e.preventDefault()}
          >
            <div className="flex-none p-4 border-b flex items-center justify-between bg-white">
              <h2 className="text-lg font-semibold text-gray-800">Transfer Documents</h2>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <SearchParticipants onSelect={startTransfer} />
            </div>
            <div className="flex-none p-4 border-t bg-gray-50 rounded-b-lg">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setTransferDocumentModal(false)}
              >
                Cancel
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};
